import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/ui/button.js'
import { TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { Dialog } from '../components/ui/dialog.js'
import { MergeDialog } from '../components/merge-dialog.js'
import { ValueListItemDialog } from '../components/value-list-item-dialog.js'
import { format, t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import {
  approveEntry,
  listPendingEntries,
  rejectEntry,
  type PendingEntry,
} from '../lib/value-lists.js'

type Dialog =
  | { kind: 'none' }
  | { kind: 'edit'; entry: PendingEntry }
  | { kind: 'merge'; entry: PendingEntry }
  | { kind: 'reject'; entry: PendingEntry }

/**
 * Entries a capturer created in the field.
 *
 * Until they are reviewed they exist only on the device that made them and here
 * — which is the point: work at the stand never blocks on someone at a desk.
 */
export function PendingPage() {
  const strings = t()
  const queryClient = useQueryClient()

  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' })
  const [notice, setNotice] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const query = useQuery({ queryKey: ['pending-entries'], queryFn: listPendingEntries })

  const approve = useMutation({
    mutationFn: (entry: PendingEntry) => approveEntry(entry.valueListId, entry.id),
    onSuccess: async () => {
      setNotice(strings.pending.approve.success)
      await queryClient.invalidateQueries({ queryKey: ['pending-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
    },
  })

  const reject = useMutation({
    mutationFn: (entry: PendingEntry) =>
      rejectEntry(entry.valueListId, entry.id, rejectReason || undefined),
    onSuccess: async () => {
      setNotice(strings.pending.reject.success)
      setDialog({ kind: 'none' })
      setRejectReason('')
      await queryClient.invalidateQueries({ queryKey: ['pending-entries'] })
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
    },
  })

  const mutationError = approve.error ?? reject.error

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-xl font-semibold">{strings.pending.heading}</h1>
        <p className="text-text-muted mt-1 max-w-2xl text-sm">{strings.pending.intro}</p>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}

      {mutationError && (
        <Alert tone="danger">
          {mutationError instanceof ApiError
            ? (mutationError.problem.detail ?? strings.errors.unexpected)
            : strings.errors.unexpected}
        </Alert>
      )}

      {query.isError && <Alert tone="danger">{strings.pending.loadError}</Alert>}
      {query.isPending && <p className="text-text-muted text-sm">{strings.app.loading}</p>}

      {query.data && query.data.length === 0 && (
        <p className="text-text-muted text-sm">{strings.pending.empty}</p>
      )}

      {query.data && query.data.length > 0 && (
        <div className="border-border overflow-x-auto rounded-lg border">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-surface-sunken text-text-muted">
              <tr>
                <th scope="col" className="px-4 py-2.5 text-left font-medium">
                  {strings.pending.columns.list}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-medium">
                  {strings.pending.columns.label}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-medium">
                  {strings.pending.columns.createdBy}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-medium">
                  {strings.pending.columns.createdAt}
                </th>
                <th scope="col" className="px-4 py-2.5 text-right font-medium">
                  {strings.pending.columns.usage}
                </th>
                <th scope="col" className="px-4 py-2.5 text-right font-medium">
                  {strings.valueLists.columns.actions}
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((entry) => (
                <tr key={entry.id} className="border-border border-t">
                  <td className="px-4 py-2.5">
                    <Badge tone="info">{entry.valueListName}</Badge>
                  </td>
                  <td className="px-4 py-2.5 font-medium">{entry.label}</td>
                  <td className="text-text-muted px-4 py-2.5">{entry.createdByName ?? '—'}</td>
                  <td className="text-text-muted px-4 py-2.5">
                    {new Date(entry.createdAt).toLocaleString('de-DE')}
                  </td>
                  <td className="text-text-muted px-4 py-2.5 text-right">{entry.usageCount}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={approve.isPending}
                        onClick={() => {
                          setNotice(null)
                          approve.mutate(entry)
                        }}
                      >
                        {strings.pending.actions.approve}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDialog({ kind: 'edit', entry })}
                      >
                        {strings.pending.actions.editAndApprove}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDialog({ kind: 'merge', entry })}
                      >
                        {strings.pending.actions.merge}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setRejectReason('')
                          setDialog({ kind: 'reject', entry })
                        }}
                      >
                        {strings.pending.actions.reject}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editing a pending entry approves it in the same step. */}
      <ValueListItemDialog
        open={dialog.kind === 'edit'}
        listId={dialog.kind === 'edit' ? dialog.entry.valueListId : ''}
        isSupplierList={dialog.kind === 'edit' ? dialog.entry.valueListKey === 'suppliers' : false}
        item={dialog.kind === 'edit' ? dialog.entry : null}
        onClose={() => setDialog({ kind: 'none' })}
        onSaved={() => {
          if (dialog.kind === 'edit') approve.mutate(dialog.entry)
          setDialog({ kind: 'none' })
        }}
      />

      <MergeDialog
        open={dialog.kind === 'merge'}
        listId={dialog.kind === 'merge' ? dialog.entry.valueListId : ''}
        source={dialog.kind === 'merge' ? dialog.entry : null}
        onClose={() => setDialog({ kind: 'none' })}
        onMerged={(message) => {
          setNotice(message)
          setDialog({ kind: 'none' })
        }}
      />

      <Dialog
        open={dialog.kind === 'reject'}
        onClose={() => setDialog({ kind: 'none' })}
        title={strings.pending.reject.heading}
        description={
          dialog.kind === 'reject'
            ? format(strings.pending.reject.description, { label: dialog.entry.label })
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog({ kind: 'none' })}>
              {strings.valueLists.actions.cancel}
            </Button>
            <Button
              variant="danger"
              disabled={reject.isPending}
              onClick={() => {
                if (dialog.kind === 'reject') reject.mutate(dialog.entry)
              }}
            >
              {strings.pending.reject.submit}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {dialog.kind === 'reject' && dialog.entry.usageCount > 0 && (
            <Alert tone="warning">
              {format(strings.pending.reject.warning, { count: dialog.entry.usageCount })}
            </Alert>
          )}

          <TextField
            label={strings.pending.reject.reason}
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
          />
        </div>
      </Dialog>
    </div>
  )
}
